import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpen,
  Brain,
  Compass,
  FileText,
  GraduationCap,
  LineChart,
  ListChecks,
  PencilLine,
  Sparkles,
  ShieldCheck,
  Timer,
  type LucideIcon,
} from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { HeroScene } from "@/platform/ui/illustrations/hero-scene";
import { BookStack, BrainSpot, ClockSpot, SparkleCluster, TargetSpot, TrophySpot } from "@/platform/ui/illustrations/spot";
import { Blob, SoftStar, Squircle } from "@/platform/ui/illustrations/blobs";

const examWeights = [
  { label: "Redação Vunesp", value: 20, note: "Maior bloco individual", color: "pink" as const },
  { label: "Discursivas", value: 30, note: "Bio · Quím · Fís · Mat · Inglês", color: "violet" as const },
  { label: "Português objetivo", value: 10, note: "Eixo transversal", color: "yellow" as const },
  { label: "Matemática objetiva", value: 10, note: "+6 com a discursiva", color: "mint" as const },
  { label: "Inglês objetivo", value: 5, note: "11 pts somando discursiva", color: "coral" as const },
  { label: "Biologia objetiva", value: 5, note: "Saúde e biotec", color: "sky" as const },
];

const phases: Array<{
  icon: LucideIcon;
  kicker: string;
  title: string;
  description: string;
  tone: "violet" | "pink" | "mint" | "yellow" | "coral" | "sky";
}> = [
  {
    icon: Compass,
    kicker: "01",
    title: "Diagnóstico",
    description:
      "Mini-prova objetiva, redação curta e duas discursivas. Foto inicial calibrada por psicometria.",
    tone: "violet",
  },
  {
    icon: ListChecks,
    kicker: "02",
    title: "Plano adaptativo",
    description:
      "Prioriza ganho marginal: peso na prova × lacuna individual × tempo restante.",
    tone: "pink",
  },
  {
    icon: PencilLine,
    kicker: "03",
    title: "Redação Vunesp",
    description:
      "Rubrica oficial. Detector de modelo pronto. Texto ou foto manuscrita.",
    tone: "yellow",
  },
  {
    icon: FileText,
    kicker: "04",
    title: "Discursivas",
    description:
      "Bio, Quím, Fís, Mat e Inglês com rubrica por disciplina, resposta mínima e excelente.",
    tone: "mint",
  },
  {
    icon: Timer,
    kicker: "05",
    title: "Simulado completo",
    description:
      "Cinco horas no formato Einstein. Nota padronizada estimada e posição relativa simulada.",
    tone: "coral",
  },
  {
    icon: LineChart,
    kicker: "06",
    title: "Evolução",
    description:
      "Série temporal de acerto, gargalos por disciplina, mapa de atividade.",
    tone: "sky",
  },
];

const TONE_BG: Record<string, string> = {
  violet: "bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent-soft-foreground)]",
  pink: "bg-[color:var(--color-pink-soft)] text-[color:var(--color-pink-deep)]",
  mint: "bg-[color:var(--color-mint-soft)] text-[color:var(--color-mint-deep)]",
  yellow: "bg-[color:var(--color-yellow-soft)] text-[color:var(--color-yellow-deep)]",
  coral: "bg-[color:var(--color-coral-soft)] text-[color:var(--color-coral-deep)]",
  sky: "bg-[color:var(--color-sky-soft)] text-[color:var(--color-sky-deep)]",
};

const TONE_BAR: Record<string, string> = {
  violet: "bg-[color:var(--color-accent)]",
  pink: "bg-[color:var(--color-pink)]",
  mint: "bg-[color:var(--color-mint)]",
  yellow: "bg-[color:var(--color-yellow-deep)]",
  coral: "bg-[color:var(--color-coral)]",
  sky: "bg-[color:var(--color-sky)]",
};

const principles: Array<{ icon: LucideIcon; title: string; body: string; tone: keyof typeof TONE_BG }> = [
  {
    icon: Brain,
    title: "Psicometria, não palpite",
    body: "Cada questão atualiza um vetor de proficiência. Plano vem do modelo, não de regra de bolso.",
    tone: "violet",
  },
  {
    icon: GraduationCap,
    title: "Padrão Vunesp por dentro",
    body: "Engenharia reversa de distratores, enunciados e redação Vunesp.",
    tone: "pink",
  },
  {
    icon: ShieldCheck,
    title: "Honesto até o fim",
    body: "Sem promessa de aprovação. Diferenciamos evidência, hipótese e recomendação.",
    tone: "mint",
  },
];

export function EinsteinLandingPage() {
  return (
    <>
      {/* ───────────────────── HERO ───────────────────── */}
      <section className="relative overflow-hidden bg-mesh">
        {/* Blobs decorativos atrás */}
        <Blob
          color="violet"
          className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-30 blur-2xl animate-blob"
        />
        <Blob
          color="pink"
          className="pointer-events-none absolute -right-24 top-40 w-80 opacity-30 blur-2xl animate-blob"
        />
        <SoftStar
          color="yellow"
          className="pointer-events-none absolute right-10 bottom-20 w-24 opacity-60 animate-float"
        />

        <Container size="wide" className="relative pt-24 pb-28 md:pt-32 md:pb-32">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-center">
            <div className="animate-fade-up">
              <Badge variant="accentSolid" className="w-fit gap-2">
                <Sparkles className="size-3" aria-hidden />
                Vestibular Psicologia · Einstein · Vunesp
              </Badge>

              <h1 className="mt-8 font-display text-[length:var(--text-display-xl)] font-semibold leading-[var(--text-display-xl--line-height)] tracking-[var(--text-display-xl--letter-spacing)] text-balance">
                Não estude Psicologia.
                <br />
                <span className="text-gradient-accent">Estude o jeito Vunesp</span>
                <br />
                <span className="text-[color:var(--color-foreground)]">de selecionar.</span>
              </h1>

              <p className="mt-8 max-w-xl text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
                A prova do Einstein vale 100 pontos: 50 em objetivas, 30 em discursivas, 20 em
                redação. Metade da seleção está fora da múltipla escolha. Aqui você treina
                exatamente esse padrão.
              </p>

              <div className="mt-10 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                <Button asChild size="xl" variant="gradient">
                  <Link to="/cadastro">
                    Começar diagnóstico
                    <ArrowUpRight className="size-4" aria-hidden />
                  </Link>
                </Button>
                <Button asChild size="xl" variant="outline">
                  <Link to="/metodo">Como funciona</Link>
                </Button>
              </div>

              {/* Stat row */}
              <dl className="mt-14 grid grid-cols-3 gap-4 md:gap-6">
                {[
                  { k: "100", l: "pontos", tone: "violet" as const, icon: TargetSpot },
                  { k: "5h", l: "fase única", tone: "pink" as const, icon: ClockSpot },
                  { k: "500", l: "média padrão", tone: "mint" as const, icon: TrophySpot },
                ].map(({ k, l, tone, icon: Icon }) => (
                  <div
                    key={l}
                    className="group rounded-xl border border-[--color-border] bg-[color:var(--color-card)] p-5 shadow-[var(--shadow-sm)] lift"
                  >
                    <Icon className="size-10 -ml-1" />
                    <dt
                      data-tabular="true"
                      className="mt-2 font-display text-[length:var(--text-display-md)] font-bold leading-none tracking-[var(--text-display-md--letter-spacing)]"
                    >
                      {k}
                    </dt>
                    <dd className="mt-1 text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                      {l}
                    </dd>
                    <span aria-hidden className={`mt-3 block h-1 w-10 rounded-full ${TONE_BAR[tone]}`} />
                  </div>
                ))}
              </dl>
            </div>

            {/* Painel direito — ilustração hero + card de peso */}
            <div className="animate-fade-up [animation-delay:120ms] relative">
              <HeroScene className="absolute -top-10 -right-6 w-[440px] opacity-95 hidden lg:block" />

              <div className="relative mt-16 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] shadow-[var(--shadow-md)] lg:mt-40">
                <div className="flex items-center justify-between border-b border-[--color-border] px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className="chip size-7 bg-[color:var(--color-pink)] text-white">
                      <BookOpen className="size-3.5" aria-hidden />
                    </span>
                    <span className="font-display text-[length:var(--text-body-sm)] font-semibold">
                      Peso real por área
                    </span>
                  </div>
                  <Badge variant="accentSolid">100 pt</Badge>
                </div>

                <ul className="space-y-3 px-6 py-5">
                  {examWeights.map((row, i) => (
                    <li
                      key={row.label}
                      className="grid grid-cols-[1fr_auto] items-center gap-3 animate-fade-up"
                      style={{ animationDelay: `${200 + i * 50}ms` }}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-[length:var(--text-body-sm)] font-medium">
                          {row.label}
                        </p>
                        <p className="truncate text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                          {row.note}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="relative h-2 w-24 overflow-hidden rounded-full bg-[color:var(--color-muted)]">
                          <div
                            className={`absolute inset-y-0 left-0 rounded-full ${TONE_BAR[row.color]}`}
                            style={{ width: `${(row.value / 30) * 100}%` }}
                          />
                        </div>
                        <span
                          data-tabular="true"
                          className="w-8 text-right text-[length:var(--text-body-sm)] font-semibold"
                        >
                          {row.value}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ───────────────────── PRINCÍPIOS ───────────────────── */}
      <section className="relative">
        <Squircle
          color="yellow"
          className="pointer-events-none absolute -left-12 top-20 w-24 opacity-40 rotate-12 animate-float"
        />
        <Container size="wide" className="py-24">
          <div className="grid gap-8 md:grid-cols-3">
            {principles.map(({ icon: Icon, title, body, tone }, i) => (
              <article
                key={title}
                className="group rounded-xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)] lift"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <span className={`chip size-12 ${TONE_BG[tone]}`}>
                  <Icon className="size-5" aria-hidden />
                </span>
                <h3 className="mt-5 font-display text-[length:var(--text-h2)] font-semibold leading-[var(--text-h2--line-height)] tracking-[var(--text-h2--letter-spacing)] text-balance">
                  {title}
                </h3>
                <p className="mt-3 text-[length:var(--text-body)] leading-[var(--text-body--line-height)] text-[color:var(--color-muted-foreground)] text-pretty">
                  {body}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* ───────────────────── MÉTODO ───────────────────── */}
      <section className="relative border-y border-[--color-border] bg-paper">
        <SparkleCluster className="pointer-events-none absolute right-10 top-10 w-24 opacity-80" />
        <Container size="wide" className="py-24 md:py-32">
          <div className="grid items-end gap-8 md:grid-cols-[2fr_1fr]">
            <div className="max-w-2xl">
              <Badge variant="pink" className="w-fit">
                <Sparkles className="size-3" aria-hidden />
                Método em 6 camadas
              </Badge>
              <h2 className="mt-5 font-display text-[length:var(--text-display-lg)] font-semibold leading-[var(--text-display-lg--line-height)] tracking-[var(--text-display-lg--letter-spacing)] text-balance">
                Da foto inicial ao simulado de cinco horas.
              </h2>
              <p className="mt-5 text-[length:var(--text-body-lg)] leading-[var(--text-body-lg--line-height)] text-[color:var(--color-muted-foreground)] text-pretty">
                Cada camada é mensurável, auditável e calibrada por psicometria.
              </p>
            </div>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {phases.map(({ icon: Icon, kicker, title, description, tone }, i) => (
              <article
                key={title}
                className="group relative overflow-hidden rounded-xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)] lift animate-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="flex items-start justify-between">
                  <span className={`chip size-12 ${TONE_BG[tone]}`}>
                    <Icon className="size-5" aria-hidden />
                  </span>
                  <span
                    data-tabular="true"
                    className="font-display text-[length:var(--text-h2)] font-bold opacity-15"
                  >
                    {kicker}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-[length:var(--text-h2)] font-semibold leading-[var(--text-h2--line-height)] tracking-[var(--text-h2--letter-spacing)]">
                  {title}
                </h3>
                <p className="mt-3 text-[length:var(--text-body-sm)] leading-[var(--text-body-sm--line-height)] text-[color:var(--color-muted-foreground)] text-pretty">
                  {description}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* ───────────────────── BANCA + ANATOMIA ───────────────────── */}
      <section className="relative">
        <Blob
          color="mint"
          className="pointer-events-none absolute -right-32 top-20 w-96 opacity-25 blur-2xl"
        />
        <Container size="wide" className="py-24 md:py-32">
          <div className="grid gap-16 lg:grid-cols-[1fr_1.1fr] lg:items-start">
            <div>
              <Badge variant="coral" className="w-fit">
                Por dentro da banca
              </Badge>
              <h2 className="mt-5 font-display text-[length:var(--text-display-lg)] font-semibold leading-[var(--text-display-lg--line-height)] tracking-[var(--text-display-lg--letter-spacing)] text-balance">
                A Vunesp <span className="text-gradient-accent">não cobra</span> como o Enem.
              </h2>
              <p className="mt-5 text-[length:var(--text-body-lg)] leading-[var(--text-body-lg--line-height)] text-[color:var(--color-muted-foreground)] text-pretty">
                Sem proposta de intervenção. Distratores plausíveis. Penalidade severa para modelo
                pronto e cópia da coletânea.
              </p>

              <ul className="mt-10 space-y-3">
                {[
                  { text: "Detector de modelo pronto na correção de redação", tone: "violet" as const },
                  { text: "Rubrica oficial Vunesp em quatro critérios", tone: "pink" as const },
                  { text: "Discursivas com resposta mínima e excelente", tone: "mint" as const },
                  { text: "Repertório curado conectado ao DNA Einstein", tone: "yellow" as const },
                ].map(({ text, tone }) => (
                  <li
                    key={text}
                    className="flex items-start gap-3 rounded-lg bg-[color:var(--color-card)] border border-[--color-border] p-4 lift"
                  >
                    <span className={`chip size-8 shrink-0 ${TONE_BG[tone]}`}>
                      <Sparkles className="size-3.5" aria-hidden />
                    </span>
                    <span className="text-[length:var(--text-body-sm)]">{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-8 md:p-10 shadow-[var(--shadow-md)]">
              <BookStack className="absolute -top-12 right-6 w-24 animate-float" />
              <Badge variant="accentSolid" className="w-fit">
                Anatomia oficial
              </Badge>
              <h3 className="mt-4 font-display text-[length:var(--text-h2)] font-semibold leading-[var(--text-h2--line-height)] tracking-[var(--text-h2--letter-spacing)]">
                Vestibular Einstein 2026
              </h3>
              <p className="mt-1 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                Curso de Psicologia · fase única.
              </p>

              <dl className="mt-8 grid grid-cols-2 gap-x-8 gap-y-6">
                <Stat label="Objetivas" value="50" suffix="50 pts" tone="violet" />
                <Stat label="Discursivas" value="5" suffix="30 pts" tone="pink" />
                <Stat label="Redação" value="1" suffix="20 pts" tone="yellow" />
                <Stat label="Tempo total" value="5h" tone="mint" />
                <Stat label="Vagas 2026" value="60" suffix="45+11" tone="coral" />
                <Stat label="Padronização" value="500" suffix="±100" tone="sky" />
              </dl>

              <div className="mt-8 flex items-center gap-2.5 border-t border-[--color-border] pt-5 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                <BookOpen className="size-3.5" aria-hidden />
                Fonte: Manual do Candidato · Vunesp 2026
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ───────────────────── PULL QUOTE ───────────────────── */}
      <section className="relative border-y border-[--color-border] bg-accent-wash">
        <Container size="wide" className="py-24 md:py-32">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mx-auto inline-flex">
              <BrainSpot className="size-16" />
            </div>
            <blockquote className="mt-6 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
              Aprovação é vantagem <span className="text-gradient-accent">relativa</span>, não
              absoluta. Quem entende isso treina o que move a nota padronizada.
            </blockquote>
            <p className="mt-10 inline-flex items-center gap-2 font-mono text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-accent)]">
              <span className="inline-block size-1.5 rounded-full bg-[color:var(--color-accent)]" aria-hidden />
              Tese central · PsiEinstein PatternLab
            </p>
          </div>
        </Container>
      </section>

      {/* ───────────────────── CTA FINAL ───────────────────── */}
      <section>
        <Container size="wide" className="py-24 md:py-32">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-accent p-10 text-white md:p-16">
            {/* Decorative shapes */}
            <Blob color="pink" className="pointer-events-none absolute -right-32 -top-32 w-96 opacity-30 blur-2xl" />
            <Blob color="yellow" className="pointer-events-none absolute -left-20 -bottom-20 w-72 opacity-25 blur-2xl" />
            <SparkleCluster className="pointer-events-none absolute right-12 top-12 w-24 opacity-60" />

            <div className="relative grid items-center gap-10 md:grid-cols-[1.4fr_1fr]">
              <div>
                <Badge variant="onDark">
                  <Sparkles className="size-3" aria-hidden />
                  Começar
                </Badge>
                <h2 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
                  Em duas horas, você sabe exatamente onde está e o que move a próxima nota.
                </h2>
                <p className="mt-5 max-w-xl text-[length:var(--text-body-lg)] leading-[var(--text-body-lg--line-height)] text-white/80 text-pretty">
                  Diagnóstico calibrado, três focos prioritários, primeiro plano semanal pronto.
                </p>
                <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                  <Button asChild size="xl" variant="onDark">
                    <Link to="/cadastro">
                      Começar diagnóstico
                      <ArrowUpRight className="size-4" aria-hidden />
                    </Link>
                  </Button>
                  <Button asChild size="xl" variant="onDarkOutline">
                    <Link to="/precos">Ver planos</Link>
                  </Button>
                </div>
              </div>

              <div className="hidden md:flex justify-center">
                <TrophySpot className="w-56 drop-shadow-xl animate-float" />
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}

type BadgeTone = "accent" | "pink" | "mint" | "yellow" | "coral" | "sky";
const TONE_TO_BADGE: Record<keyof typeof TONE_BG, BadgeTone> = {
  violet: "accent",
  pink: "pink",
  mint: "mint",
  yellow: "yellow",
  coral: "coral",
  sky: "sky",
};

function Stat({
  label,
  value,
  suffix,
  tone,
}: {
  label: string;
  value: string;
  suffix?: string;
  tone: keyof typeof TONE_BG;
}) {
  return (
    <div>
      <Badge variant={TONE_TO_BADGE[tone]} className="mb-2">
        {label}
      </Badge>
      <dd
        data-tabular="true"
        className="font-display text-[length:var(--text-h1)] font-bold tracking-[var(--text-h1--letter-spacing)]"
      >
        {value}
        {suffix ? (
          <span className="ml-2 text-[length:var(--text-body-sm)] font-normal text-[color:var(--color-muted-foreground)]">
            {suffix}
          </span>
        ) : null}
      </dd>
    </div>
  );
}
